import React, { useState } from 'react';
import TcgCardPickerModal from './TcgCardPickerModal';
import { formatPrice, formatDate, cleanCardName, buildCardMarketUrl, buildTcgPlayerUrl } from '../../utils/formatters';
import { RARITIES, RARITY_MAP, LANGUAGE_OPTIONS } from '../../utils/constants';

export default function CardDetailModal({ card, onClose, updateCard, deleteCard }) {
  const [editing, setEditing] = useState(null); // field name being edited
  const [editValue, setEditValue] = useState('');
  const [showPicker, setShowPicker] = useState(false);

  if (!card) return null;

  const isLinked = !!card.tcg_card_id;

  // Use listing image (large), fall back to TCG ref
  const largeImageUrl = card.image_url
    ? card.image_url.replace(/s-l\d+/, 's-l800')
    : card.tcg_card?.image_large || card.tcg_card?.image_small || null;

  const startEdit = (field, value) => {
    setEditing(field);
    setEditValue(value || '');
  };

  const saveEdit = async (field, value) => {
    setEditing(null);
    if (card[field] !== (value || null)) {
      await updateCard(card.id, { [field]: value || null });
    }
  };

  const cancelEdit = () => {
    setEditing(null);
  };

  const handleDelete = () => {
    if (window.confirm('Delete this card?')) {
      deleteCard(card.id);
      onClose();
    }
  };

  const renderField = (label, field, displayValue, editType = 'text') => {
    const isEditing = editing === field;

    // Linked cards: non-editable, show TCG-sourced style
    if (isLinked && field !== 'language') {
      return (
        <div className="card-detail-field">
          <span className="card-detail-label">{label}</span>
          <span className="card-detail-value tcg-sourced">
            {displayValue || '\u2014'}
          </span>
        </div>
      );
    }

    if (isEditing) {
      let input;
      if (editType === 'language') {
        input = (
          <select
            className="inline-edit-input"
            value={editValue}
            onChange={(e) => {
              setEditValue(e.target.value);
              saveEdit(field, e.target.value);
            }}
            onBlur={() => saveEdit(field, editValue)}
            onKeyDown={(e) => { if (e.key === 'Escape') cancelEdit(); }}
            autoFocus
          >
            <option value="">None</option>
            {LANGUAGE_OPTIONS.map((l) => (
              <option key={l.value} value={l.value}>{l.value} - {l.label}</option>
            ))}
          </select>
        );
      } else if (editType === 'rarity') {
        input = (
          <select
            className="inline-edit-input"
            value={editValue}
            onChange={(e) => {
              setEditValue(e.target.value);
              saveEdit(field, e.target.value);
            }}
            onBlur={() => saveEdit(field, editValue)}
            onKeyDown={(e) => { if (e.key === 'Escape') cancelEdit(); }}
            autoFocus
          >
            <option value="">None</option>
            {RARITIES.map((r) => (
              <option key={r.abbr} value={r.abbr}>{r.abbr} - {r.name}</option>
            ))}
          </select>
        );
      } else {
        input = (
          <input
            type="text"
            className="inline-edit-input"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onBlur={() => saveEdit(field, editValue)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') saveEdit(field, editValue);
              if (e.key === 'Escape') cancelEdit();
            }}
            autoFocus
          />
        );
      }
      return (
        <div className="card-detail-field">
          <span className="card-detail-label">{label}</span>
          <span className="card-detail-value">{input}</span>
        </div>
      );
    }

    return (
      <div className="card-detail-field">
        <span className="card-detail-label">{label}</span>
        <span
          className="card-detail-value editable-cell"
          onClick={() => startEdit(field, card[field])}
          title="Click to edit"
        >
          {displayValue || '\u2014'}
        </span>
      </div>
    );
  };

  const rarityDisplay = (card.display_rarity || card.rarity)
    ? (
      <span
        className={`rarity-badge rarity-${(card.display_rarity || card.rarity || '').toLowerCase()}`}
        title={RARITY_MAP[card.display_rarity || card.rarity] || card.display_rarity || card.rarity}
      >
        {card.display_rarity || card.rarity}
        {RARITY_MAP[card.display_rarity || card.rarity] ? ` - ${RARITY_MAP[card.display_rarity || card.rarity]}` : ''}
      </span>
    )
    : null;

  return (
    <>
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content card-detail-modal" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h3>{card.display_name || card.card_name || 'Card Details'}</h3>
            <button className="btn btn-sm" onClick={onClose}>&times;</button>
          </div>

          <div className="card-detail-body">
            {/* Left: Large image */}
            <div className="card-detail-image-section">
              {largeImageUrl ? (
                <img
                  src={largeImageUrl}
                  alt={card.display_name || card.card_name || 'Card'}
                  className="card-detail-image"
                />
              ) : (
                <div className="card-detail-placeholder">No Image</div>
              )}
            </div>

            {/* Right: Details */}
            <div className="card-detail-info-section">
              {renderField('Name', 'card_name', card.display_name || card.card_name)}
              {renderField('Set', 'extension', card.display_extension || card.extension)}
              {renderField('Rarity', 'rarity', rarityDisplay, 'rarity')}
              {renderField('Language', 'language',
                card.language ? <span className="language-badge">{card.language}</span> : null,
                'language'
              )}

              <div className="card-detail-divider" />

              {/* Price */}
              <div className="card-detail-field">
                <span className="card-detail-label">Price</span>
                <span className="card-detail-value">
                  <a
                    href={card.listing_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="card-detail-price-link"
                  >
                    {formatPrice(card.price, card.currency)}
                  </a>
                </span>
              </div>

              {/* Source */}
              <div className="card-detail-field">
                <span className="card-detail-label">Source</span>
                <span className="card-detail-value">
                  <span className={`source-badge source-${card.source}`}>
                    {card.source}
                  </span>
                </span>
              </div>

              {/* Date */}
              <div className="card-detail-field">
                <span className="card-detail-label">Added</span>
                <span className="card-detail-value card-detail-date">
                  {formatDate(card.scraped_at)}
                </span>
              </div>

              <div className="card-detail-divider" />

              {/* Ref Prices */}
              <div className="card-detail-field">
                <span className="card-detail-label">CardMarket</span>
                <span className="card-detail-value">
                  <a
                    href={card.cardmarket_url || buildCardMarketUrl(card)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="ref-price-link"
                  >
                    {card.cardmarket_price != null
                      ? formatPrice(card.cardmarket_price, 'EUR')
                      : <span className="ref-price-search">Search</span>
                    }
                  </a>
                </span>
              </div>

              <div className="card-detail-field">
                <span className="card-detail-label">TCGplayer</span>
                <span className="card-detail-value">
                  <a
                    href={card.tcgplayer_url || buildTcgPlayerUrl(card)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="ref-price-link"
                  >
                    {card.tcgplayer_price != null
                      ? formatPrice(card.tcgplayer_price, 'USD')
                      : <span className="ref-price-search">Search</span>
                    }
                  </a>
                </span>
              </div>

              <div className="card-detail-divider" />

              {/* TCG Reference */}
              <div className="card-detail-field">
                <span className="card-detail-label">TCG Ref</span>
                <span className="card-detail-value">
                  {isLinked ? (
                    <div className="card-detail-tcg-link">
                      <span className="tcg-link-badge linked">
                        {card.tcg_card?.set_id} #{card.tcg_card?.number}
                      </span>
                      <button
                        className="btn btn-sm btn-link"
                        onClick={() => setShowPicker(true)}
                        title="Change TCG reference"
                      >
                        Change
                      </button>
                      <button
                        className="btn btn-sm btn-danger"
                        onClick={() => updateCard(card.id, { tcg_card_id: '' })}
                        title="Unlink"
                      >
                        Unlink
                      </button>
                    </div>
                  ) : (
                    <button
                      className="btn btn-sm"
                      onClick={() => setShowPicker(true)}
                    >
                      Link to TCG
                    </button>
                  )}
                </span>
              </div>

              {/* Delete */}
              <div className="card-detail-actions">
                <button
                  className="btn btn-danger"
                  onClick={handleDelete}
                >
                  Delete Card
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* TCG Card Picker Modal (nested) */}
      {showPicker && (
        <TcgCardPickerModal
          initialSearch={card.tcg_card_id ? (card.display_name || '') : (cleanCardName(card.card_name) || '')}
          initialArtist={card.display_illustrator || card.illustrator || ''}
          initialExtension={card.display_extension || card.extension || ''}
          initialRarity={card.display_rarity || card.rarity || ''}
          cardImageUrl={card.image_url || null}
          cardTitle={card.card_name || ''}
          cardPrice={card.price}
          cardCurrency={card.currency}
          onSelect={async (tcgCardId) => {
            await updateCard(card.id, { tcg_card_id: tcgCardId });
            setShowPicker(false);
          }}
          onClose={() => setShowPicker(false)}
        />
      )}
    </>
  );
}
